/*  useBlog — thin fetch hooks for the Blog subsystem.
    ────────────────────────────────────────────────────────────────
    - useBlogList({ audience, category, search, page })  — paginated listing
    - useBlogCategories()                                — categories w/ counts
    - useBlogPost(slug)                                  — single post + related
*/
import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;


export function useBlogList({ audience, category, search, page = 1, perPage = 12 }) {
    const [state, setState] = useState({
        items: [], total: 0, has_more: false, loading: true, error: null,
    });

    const fetchList = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const { data } = await axios.get(`${API}/api/blog`, {
                params: {
                    audience: audience || undefined,
                    category: category || undefined,
                    search: search || undefined,
                    page,
                    per_page: perPage,
                },
            });
            setState({
                items: data.items || [],
                total: data.total || 0,
                has_more: !!data.has_more,
                loading: false,
                error: null,
            });
        } catch (err) {
            setState((s) => ({ ...s, loading: false, error: err.message || "Failed to load posts" }));
        }
    }, [audience, category, search, page, perPage]);

    useEffect(() => { fetchList(); }, [fetchList]);
    return { ...state, refetch: fetchList };
}


export function useBlogCategories() {
    const [state, setState] = useState({ categories: [], loading: true });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/blog/categories`);
                if (!cancelled) setState({ categories: data.categories || [], loading: false });
            } catch {
                if (!cancelled) setState({ categories: [], loading: false });
            }
        })();
        return () => { cancelled = true; };
    }, []);
    return state;
}


export function useBlogPost(slug) {
    const [state, setState] = useState({ post: null, related: [], loading: true, error: null });
    useEffect(() => {
        let cancelled = false;
        setState({ post: null, related: [], loading: true, error: null });
        if (!slug) return () => { cancelled = true; };
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/blog/${slug}`);
                if (!cancelled) {
                    setState({
                        post: data.post || null,
                        related: data.related || [],
                        loading: false,
                        error: null,
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    setState({
                        post: null,
                        related: [],
                        loading: false,
                        error: err.response?.status === 404 ? "not_found" : (err.message || "load_error"),
                    });
                }
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);
    return state;
}
